import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Timezone } from "@shared/types/commonTypes";

type UseTimezonesResult = {
  timezones: Timezone[];
  isLoading: boolean;
  error: Error | null;
};

/**
 * Hook to fetch and cache timezones data.
 * Timezones are static metadata, cached indefinitely.
 * 
 * Note: This is in-memory cache (per session). Data is cleared on page refresh.
 */
export default function useTimezones(): UseTimezonesResult {
  const { GET } = useApiRequest();
  const { i18n } = useTranslation();
  
  const {
    data: timezones = [],
    isLoading,
    error,
  } = useQuery<Timezone[]>({
    // Include language in queryKey to cache per language
    queryKey: ["timezones", i18n.language],
    queryFn: () => GET("/api/metadata/timezones"),
    // Timezones are static metadata - cache indefinitely (in-memory)
    staleTime: Infinity,
    // Keep in memory for 30 days even when not actively used (garbage collection)
    gcTime: 30 * 24 * 60 * 60 * 1000, // 30 days
  });

  // Sort timezones by offset (UTC-12 to UTC+14) - memoized to prevent infinite loops
  const sortedTimezones = useMemo(
    () => [...timezones].sort((a, b) => {
      // Primary sort: by offset (negative to positive)
      if (a.offset !== b.offset) {
        return a.offset - b.offset;
      }
      // Secondary sort: by label alphabetically (for same offset)
      return a.label.localeCompare(b.label);
    }),
    [timezones]
  );

  return {
    timezones: sortedTimezones,
    isLoading,
    error: error ?? null,
  } satisfies UseTimezonesResult;
}

